import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { formatAbortedReason } from '@/features/jobs/lib/format-aborted-reason'
import { ABORTED_REASONS } from '@/features/jobs/lib/job-outcome-options'
import type { JobType } from '@/features/jobs/types/job'
import type { AbortedReason } from '@/features/jobs/types/job-detail'
import {
  apiGetAbortedJobsByPartnerReport,
  apiGetAbortedJobsReport,
} from '@/features/reports/api/api-get-jobs-report'
import { DrilldownLink } from '@/features/reports/components/drilldown-link'
import { JobsReportFiltersPanel } from '@/features/reports/components/jobs-report-filters-panel'
import { JobsReportShell } from '@/features/reports/components/jobs-report-shell'
import { ReportCategoryChart } from '@/features/reports/components/report-category-chart'
import { useJobsReportPage } from '@/features/reports/hooks/use-jobs-report-page'
import { buildJobsReportBaseColumns } from '@/features/reports/lib/build-jobs-report-base-columns'
import { formatJobsReportFilterTitle } from '@/features/reports/lib/format-jobs-report-filter-title'
import { nextJobsReportSort } from '@/features/reports/lib/jobs-report-sort'
import {
  abortedJobsByPartnerPath,
  abortedJobsByReasonPath,
  buildAbortedJobsReportSearchParams,
  defaultAbortedJobsReportSearchParams,
  parseAbortedJobsReportSearchParams,
} from '@/features/reports/lib/jobs-report-search-params'
import {
  emptyAbortedJobsReportFilters,
  type AbortedJob,
  type AbortedJobsReportFilters,
  type AbortedJobsReportSummary,
  type JobsByPartnerReportSummary,
} from '@/features/reports/types/jobs-report'
import {
  ListTable,
  LoadingSpinner,
  PaginationLinks,
  SelectField,
  type ListTableColumn,
  type SelectOption,
} from '@/shared/components'

type AbortedReportSummary = AbortedJobsReportSummary | JobsByPartnerReportSummary

const emptySummary = (): AbortedReportSummary => ({
  by_aborted_reason: [],
})

const abortedReasonOptions: SelectOption[] = ABORTED_REASONS.map(
  ({ value, label }) => ({
    value,
    label,
  })
)

const isPartnerSummary = (
  summary: AbortedReportSummary
): summary is JobsByPartnerReportSummary => 'by_partner' in summary

const extraPartnerRows = (reportSummary: AbortedReportSummary) =>
  isPartnerSummary(reportSummary) ? reportSummary.by_partner : []

export const AbortedJobsReportPage = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isPartnerView = pathname === '/reports/aborted-jobs-by-partner'

  const {
    filters,
    page,
    chartType,
    sort,
    jobs,
    summary,
    pagination,
    isInitialLoading,
    error,
    isFiltersOpen,
    setIsFiltersOpen,
    updateSearch,
    chartTitle,
  } = useJobsReportPage<
    AbortedJobsReportFilters,
    AbortedJob,
    AbortedReportSummary
  >({
    parseSearchParams: parseAbortedJobsReportSearchParams,
    buildSearchParams: buildAbortedJobsReportSearchParams,
    defaultSearchParams: defaultAbortedJobsReportSearchParams,
    fetchReport: isPartnerView
      ? apiGetAbortedJobsByPartnerReport
      : apiGetAbortedJobsReport,
    emptySummary,
    formatFilterTitle: (nextFilters, labels) =>
      formatJobsReportFilterTitle(
        nextFilters,
        labels,
        nextFilters.aborted_reason
          ? `Aborted reason: ${formatAbortedReason(nextFilters.aborted_reason)}`
          : undefined
      ),
    loadErrorMessage: isPartnerView
      ? 'Failed to load aborted jobs by partner report'
      : 'Failed to load aborted jobs report',
    extraPartnerRows,
  })

  const reasonTo = (abortedReason: AbortedReason) =>
    abortedJobsByPartnerPath(
      { ...filters, aborted_reason: abortedReason },
      1,
      chartType,
      sort
    )

  const partnerTo = (assignedPartnerId: number) =>
    abortedJobsByReasonPath(
      { ...filters, assigned_partner_id: assignedPartnerId },
      1,
      chartType,
      sort
    )

  const jobTypeTo = (jobType: JobType) => {
    const path = isPartnerView
      ? abortedJobsByPartnerPath
      : abortedJobsByReasonPath

    return path({ ...filters, job_type: jobType }, 1, chartType, sort)
  }

  const columns: ListTableColumn<AbortedJob>[] = buildJobsReportBaseColumns({
    filters,
    partnerTo,
    jobTypeTo,
    primaryColumns: [
      {
        id: 'aborted_reason',
        header: 'Aborted reason',
        cell: (job) => {
          const abortedReason = job.aborted_reason
          if (!abortedReason) {
            return ''
          }

          return (
            <DrilldownLink
              label={formatAbortedReason(abortedReason)}
              active={filters.aborted_reason === abortedReason}
              to={reasonTo(abortedReason)}
            />
          )
        },
      },
    ],
  })

  const chartMatchesView = isPartnerView
    ? isPartnerSummary(summary)
    : !isPartnerSummary(summary)
  const isChartLoading = isInitialLoading || !chartMatchesView

  const tableContent = (() => {
    if (isInitialLoading) {
      return <LoadingSpinner position="center" label="Loading aborted jobs" />
    }

    if (error) {
      return (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )
    }

    return (
      <>
        <ListTable
          columns={columns}
          data={jobs}
          getRowKey={(job) => job.id}
          emptyMessage="No aborted jobs found."
          onRowClick={(job) => navigate(`/jobs/${job.id}`)}
          sort={{ columnId: sort.column, direction: sort.direction }}
          onSort={(columnId) =>
            updateSearch(filters, 1, chartType, nextJobsReportSort(sort, columnId))
          }
        />

        <PaginationLinks
          pagination={pagination}
          onPageChange={(nextPage) => updateSearch(filters, nextPage)}
          className="pt-4"
        />
      </>
    )
  })()

  const chartRows = isPartnerSummary(summary)
    ? summary.by_partner.map((row) => ({
        key: String(row.assigned_partner_id),
        label: row.assigned_partner_name,
        count: row.count,
      }))
    : summary.by_aborted_reason.map((row) => ({
        key: row.aborted_reason,
        label: formatAbortedReason(row.aborted_reason),
        count: row.count,
      }))

  return (
    <>
      <JobsReportShell
        chartAriaLabel={
          isPartnerView
            ? 'SiteWorks partner breakdown'
            : 'Aborted reason breakdown'
        }
        chartTitle={chartTitle}
        isLoading={isChartLoading}
        error={error}
        onOpenFilters={() => setIsFiltersOpen(true)}
        chart={
          <ReportCategoryChart
            rows={chartRows}
            chartType={chartType}
            onChartTypeChange={(nextChartType) =>
              updateSearch(filters, page, nextChartType)
            }
            onCategoryClick={(key) =>
              navigate(
                isPartnerView
                  ? partnerTo(Number(key))
                  : reasonTo(key as AbortedReason)
              )
            }
            emptyMessage="No aborted jobs match the current filters."
          />
        }
        table={tableContent}
        filtersPanel={
          <JobsReportFiltersPanel
            open={isFiltersOpen}
            onOpenChange={setIsFiltersOpen}
            filters={filters}
            onApply={(nextFilters) => updateSearch(nextFilters, 1)}
            title="Filter aborted jobs"
            idPrefix="aborted-jobs-filter"
            emptyFilters={emptyAbortedJobsReportFilters}
            renderPrimaryDimension={(draft, setDraft) => (
              <SelectField
                id="aborted-jobs-filter-aborted-reason"
                label="Aborted reason"
                value={draft.aborted_reason}
                onValueChange={(aborted_reason) =>
                  setDraft((current) => ({
                    ...current,
                    aborted_reason: aborted_reason as AbortedReason | undefined,
                  }))
                }
                options={abortedReasonOptions}
                placeholder="All"
                allowEmpty
              />
            )}
          />
        }
      />
      <Outlet />
    </>
  )
}
